import { FC } from 'react';
import classNames from 'classnames';
import moment from 'moment/moment';
import CheckCircleIcon from '@/assets/Icons/Check-Circle.svg?react';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import Modal from '@/components/ui/Modal/Modal.tsx';
import Spinner from '@/components/ui/Spinner/Spinner.tsx';
import LogForm, { DataComplianceLogDefaults } from '@/pages/MyClimate/TripEmissionLog/LogForm.tsx';
import styles from './TripEmissionLog.module.scss';
import TravelLogType = App.Enums.TravelLogType;
import DataComplianceUserItineraryData = App.Data.DataComplianceUserItineraryData;

type ModalStepAnimationState = 'idle' | 'exit' | 'hidden' | 'enter';

interface TripDetailsModalProps {
	name: string;
	selectedItinerary?: DataComplianceUserItineraryData;
	step: number;
	tripMode: TravelLogType;
	logFormDefaults: DataComplianceLogDefaults;
	completedRowKey: string | null;
	showSpinner: boolean;
	animationState: ModalStepAnimationState;
	onClose: () => void;
	onCloseButtonClick: () => void;
	onShowAccommodationForm: () => void;
	onShowFlightForm: () => void;
	onShowTransportForm: (transportType?: DataComplianceLogDefaults['groundTransportationType']) => void;
	onDetailsNotNeeded: () => void;
	onDetailsSubmitted: () => void;
	onSubmittingChange: (isSubmitting: boolean) => void;
	onStepBack: () => void;
}

const detailEntryTitles: Record<TravelLogType, string> = {
	flight: 'Add flight details',
	accommodation: 'Add accommodation details',
	mileage: 'Add ground transport details',
};

const TripDetailsModal: FC<TripDetailsModalProps> = ({
	name,
	selectedItinerary,
	step,
	tripMode,
	logFormDefaults,
	completedRowKey,
	showSpinner,
	animationState,
	onClose,
	onCloseButtonClick,
	onShowAccommodationForm,
	onShowFlightForm,
	onShowTransportForm,
	onDetailsNotNeeded,
	onDetailsSubmitted,
	onSubmittingChange,
	onStepBack,
}) => {
	const stepProgress = completedRowKey || step === 2 ? 100 : 50;
	const tripDateRange = selectedItinerary
		? `${moment(selectedItinerary.startAt).format('DD.MM')} – ${moment(selectedItinerary.endAt).format('DD.MM.YY')}`
		: '';

	return (
		<Modal
			name={name}
			className={classNames(
				styles.dataComplianceLogModal,
				step === 2 && (tripMode === 'flight' || tripMode === 'mileage') && styles.routeDropdownLogModal,
			)}
			closeable={!showSpinner}
			onClose={onClose}
			outsideClickEnabled={!showSpinner}
		>
			{selectedItinerary && (
				<div className={styles.modalContent}>
					{showSpinner && <Spinner absolute overlay className={styles.modalSpinner} />}
					<div className={styles.modalStepProgress} aria-label={`Step ${completedRowKey ? 2 : step} of 2`}>
						<span style={{ width: `${stepProgress}%` }} />
					</div>
					<div className={styles.modalHeader}>
						<h3>
							{completedRowKey
								? 'Trip details complete'
								: step === 2
									? detailEntryTitles[tripMode]
									: 'Resolve missing trip details'}
						</h3>
						<div className={styles.modalTripData}>
							<span>{selectedItinerary.travelerName || '–'}</span>
							<span>{selectedItinerary.city}</span>
							<span>{tripDateRange}</span>
						</div>
					</div>
					<div
						className={classNames(
							styles.modalStepTransition,
							(step === 1 || completedRowKey) && styles.modalStepCentered,
							animationState === 'exit' && styles.modalStepExit,
							animationState === 'hidden' && styles.modalStepHidden,
							animationState === 'enter' && styles.modalStepEnter,
						)}
					>
						{completedRowKey ? (
							<div className={styles.completionSuccess}>
								<CheckCircleIcon className={styles.completionSuccessIcon} />
								<p>All required details for this trip have been provided.</p>
								<p className={styles.completionSuccessReminder}>
									Your update helps keep trip data complete and CO₂ reporting reliable.
								</p>
								<p className={styles.completionSuccessReminder}>
									Remember, booking with Goodwings helps capture these details automatically and
									reduces manual follow-up.
								</p>
								<button className={styles.completionCloseButton} onClick={onCloseButtonClick}>
									Close
								</button>
							</div>
						) : step === 1 ? (
							<div className={styles.completionStep}>
								{selectedItinerary.complianceGapType === 'missing-hotel' ? (
									<>
										<p>Was accommodation used for this trip?</p>
										<FlexBlock flexDirection="column" rowGap="8px" className={styles.questions}>
											<button onClick={onShowAccommodationForm}>
												Yes, add accommodation details
											</button>
											<button onClick={onDetailsNotNeeded}>
												No, accommodation was not needed
											</button>
										</FlexBlock>
									</>
								) : (
									<>
										<p>How did the traveler get to the destination?</p>
										<FlexBlock flexDirection="column" rowGap="8px" className={styles.questions}>
											<button onClick={onShowFlightForm}>By plane</button>
											<button onClick={() => onShowTransportForm('rail')}>By train</button>
											<button onClick={() => onShowTransportForm('bus')}>By bus</button>
											<button onClick={() => onShowTransportForm()}>By car or taxi</button>
											<button onClick={onDetailsNotNeeded}>
												The traveler was already at the destination
											</button>
										</FlexBlock>
									</>
								)}
							</div>
						) : (
							<LogForm
								dataCompliance
								compactDataCompliance
								tripMode={tripMode}
								dataComplianceDefaults={logFormDefaults}
								editingMode={false}
								viewMode={false}
								itineraryId={selectedItinerary.id}
								travelerId={selectedItinerary.travelerId}
								afterDataComplianceSubmitting={onDetailsSubmitted}
								onSubmittingChange={onSubmittingChange}
								showSubmittingOverlay={false}
								onStepBack={onStepBack}
							/>
						)}
					</div>
				</div>
			)}
		</Modal>
	);
};

export default TripDetailsModal;
